import React from 'react';

interface ExperienceArea {
  title: string;
  paragraphs: string[];
}

interface WorkingTheme {
  title: string;
  detail: string;
}

const EXPERIENCE_AREAS: ExperienceArea[] = [
  {
    title: 'Enterprise Banking',
    paragraphs: [
      'I built React and TypeScript interfaces for enterprise banking workflows, integrating with backend services and aggregating APIs around customer and account data. The work included eligibility and entitlement logic, validation, and explicit error and loading states so workflows stayed understandable when something failed or was still in progress.',
      'Changes moved through a distributed backend of microservices, with feature flags, accessibility considerations, and downstream fulfillment integrations. I helped refine technical stories and acceptance criteria, then followed work through development, testing, UAT, regression, and release.',
    ],
  },
  {
    title: 'Government SaaS',
    paragraphs: [
      'I developed full-stack features in an established production SaaS used for government emergency grants workflows. The work spanned React and TypeScript on the frontend and NestJS APIs and SQL on the backend, with attention to maintainability, accessibility, and testing inside a live codebase.',
      'I diagnosed and fixed production issues, improved queries and backend logic, and worked with analysts and QA so features were testable before they shipped. I also contributed to Docker-based local development, CI/CD, documentation, and mentoring on the practices already in use.',
    ],
  },
  {
    title: 'Network Operations',
    paragraphs: [
      'I built and maintained production software that supported ISP operations, including provisioning, broadband metrics, and hardware inventory. That included REST APIs and data visualizations for network and system monitoring, and keeping integrations between those systems working.',
      'When operational processes failed, I troubleshot production bugs and access issues, including on-call support. The job was to keep the software reliable enough that real operational work could continue.',
    ],
  },
];

const WORKING_THEMES: WorkingTheme[] = [
  {
    title: 'Clear ownership',
    detail: 'I take work from technical refinement through implementation and support what I ship.',
  },
  {
    title: 'Maintainable code',
    detail: 'I write code that other engineers can follow, change, and keep in production.',
  },
  {
    title: 'API and integration design',
    detail: 'I care about clear contracts between interfaces, services, and downstream systems.',
  },
  {
    title: 'Accessibility',
    detail: 'Interfaces should be usable with keyboard, assistive tech, and a clear visual hierarchy.',
  },
  {
    title: 'Testing and validation',
    detail: 'I define acceptance criteria and work with QA through testing, UAT, and regression.',
  },
  {
    title: 'Thoughtful technical decisions',
    detail: 'I prefer clarity and the smallest change that solves the problem.',
  },
  {
    title: 'Collaboration and code review',
    detail: 'I document work, review with the team, and leave the codebase easier to understand.',
  },
];

const Projects: React.FC = () => {
  return (
    <section
      id="projects"
      aria-labelledby="projects-heading"
      className="py-20 md:py-28 bg-heraldic-charcoal text-gray-100"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-16 md:mb-20">
          <p className="text-xs sm:text-sm font-medium tracking-[0.22em] uppercase text-accent mb-4">
            Engineering Experience
          </p>
          <h2
            id="projects-heading"
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6"
          >
            Building software that has to work
          </h2>
          <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-2xl">
            I&apos;ve worked across enterprise banking, government SaaS, and telecom
            operations, building and maintaining systems where reliability, clarity,
            and maintainability matter.
          </p>
        </header>

        <div className="space-y-0">
          {EXPERIENCE_AREAS.map((area) => (
            <article
              key={area.title}
              className="border-t border-heraldic-gold/25 py-12 md:py-14"
            >
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-6">
                {area.title}
              </h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                {area.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))}
        </div>

        <section
          aria-labelledby="how-i-work-heading"
          className="border-t border-heraldic-gold/25 pt-12 md:pt-14"
        >
          <h3
            id="how-i-work-heading"
            className="text-xl md:text-2xl font-semibold text-white mb-8"
          >
            How I work
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
            {WORKING_THEMES.map((theme) => (
              <div key={theme.title}>
                <dt className="text-sm font-medium tracking-wide text-accent mb-2">
                  {theme.title}
                </dt>
                <dd className="text-sm text-gray-300 leading-relaxed">{theme.detail}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </section>
  );
};

export default Projects;
